import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { first, firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AlertsFacade } from './alerts/facades/alerts.facade';
import { AUTH_CONFIG_IDS } from './auth/auth.config';
import { ScreenSizeOrientationLayerComponent } from './core/components/screen-size-orientation-layer/screen-size-orientation-layer.component';
import { AppActions } from './core/store/actions/app';
import { ConnectionStatusBannerComponent } from './shared/components/connection-status-banner/connection-status-banner.component';
import { useConnectionStatusBanner } from './shared/composables/use-connection-status-banner';
import { availableLocales, DEFAULT_LOCALE, EN_US_LOCALE } from './shared/i18n/i18n.config';
import { EndUserTokenPayload } from './shared/models/EndUserTokenPayload';
import { USER_TYPES, UserType } from './shared/models/User';
import { AnalyticsService } from './shared/services/analytics.service';
import { LocalStorageService } from './shared/services/localStorage.service';
import { SecurityService } from './shared/services/SecurityService';
import { UserEnvApi } from './shared/store/api/user-env';
import { setDefaultLocale } from './utils/l10n';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [ConnectionStatusBannerComponent, RouterOutlet, ScreenSizeOrientationLayerComponent],
})
export class AppComponent implements OnInit, OnDestroy {
    protected readonly connectionBannerStatus = useConnectionStatusBanner().status;

    private readonly translateService = inject(TranslateService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly router = inject(Router);
    private readonly analytics = inject(AnalyticsService);
    private readonly userEnvApi = inject(UserEnvApi);
    private readonly alertsFacade = inject(AlertsFacade);
    private readonly store = inject(Store);
    private readonly securityService = inject(SecurityService);
    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly titleService = inject(Title);

    private unsubscribe$: Subject<void> = new Subject();

    async ngOnInit() {
        this.routeHashedUrls();
        this.notifyNavigationToStats();

        const userType = this.getExpectedUserTypeFromCurrentPage();
        await this.setUserLanguage(userType);

        if (userType === USER_TYPES.INSTALLER && this.isInstallerPath()) {
            await this.loadInstallerEnvironment();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private routeHashedUrls() {
        this.router.events
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((event): event is NavigationStart => event instanceof NavigationStart && /^\/#/.test(event.url)),
            )
            .subscribe((event) => {
                void this.router.navigateByUrl(event.url.replace('/#', ''));
            });
    }

    private notifyNavigationToStats() {
        this.router.events
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            )
            .subscribe((e) => this.analytics.notifyNavigation(e));
    }

    private getExpectedUserTypeFromCurrentPage(): UserType {
        // i choose to use window.location rather than Angular's Router or ActivatedRoute
        // because this code seems to happen before redirection => router nor activatedRoute have '/admin' in their path/url/route
        const pathname = window.location.pathname;

        if (pathname.startsWith('/unauthorized')) {
            return USER_TYPES.ANONYMOUS;
        }

        if (pathname.startsWith('/admin') || pathname.startsWith('/portfolio')) {
            return USER_TYPES.ADMIN;
        }

        if (pathname.startsWith('/enduser') || pathname.startsWith('/query')) {
            return USER_TYPES.OWNER;
        }

        return USER_TYPES.INSTALLER;
    }

    private isInstallerPath(pathname: string = window.location.pathname): boolean {
        return (
            pathname === '/' ||
            pathname.startsWith('/sites') ||
            pathname.startsWith('/link-requests') ||
            pathname.startsWith('/alerts') ||
            pathname.startsWith('/resolve-alert') ||
            pathname.startsWith('/messages') ||
            pathname.startsWith('/notifications') ||
            pathname.startsWith('/site') ||
            pathname.startsWith('/gateway')
        );
    }

    private async loadInstallerEnvironment(): Promise<void> {
        try {
            await firstValueFrom(this.securityService.isAuthenticated(AUTH_CONFIG_IDS.INSTALLER).pipe(first(Boolean)));
            await firstValueFrom(this.userEnvApi.loadUserEnvironment());
        } catch {
            return;
        }

        this.alertsFacade.initialize();
        this.store.dispatch(AppActions.initialized());
    }

    private async setUserLanguage(userType: UserType) {
        this.translateService.setFallbackLang(DEFAULT_LOCALE);

        let userData: EndUserTokenPayload | null = null;
        if (this.isAuthenticatedUserType(userType)) {
            userData = await firstValueFrom(this.oidcSecurityService.getUserData(userType));
        }

        const locale = this.getLocale(userType, userData);

        this.translateService.use(locale);
        setDefaultLocale(locale);

        const title = await firstValueFrom(this.translateService.get('APP_PAGE_TITLE'));
        this.titleService.setTitle(`Serv-e-Go | ${title}`);
    }

    private getLocale(userType: UserType, userData: EndUserTokenPayload | null): string {
        let locale: string | undefined;

        if (userType === USER_TYPES.INSTALLER) {
            locale = this.localStorageService.getItem('locale');
        }

        return locale ?? this.getLocaleFromUserCountry(userData) ?? this.getLocaleFromBrowserLang() ?? DEFAULT_LOCALE;
    }

    private getLocaleFromUserCountry(userData: EndUserTokenPayload | null): string | undefined {
        const userCountry = userData?.cnty?.toUpperCase();
        return userCountry ? availableLocales.find((locale) => locale.endsWith(userCountry)) : undefined;
    }

    private getLocaleFromBrowserLang() {
        const preferredLocalByLang: Record<string, string> = { en: EN_US_LOCALE };
        const browserLang = this.translateService.getBrowserLang()?.split('-')[0];

        if (browserLang && preferredLocalByLang[browserLang]) {
            return preferredLocalByLang[browserLang];
        }

        return browserLang ? availableLocales.find((locale) => locale.startsWith(browserLang)) : undefined;
    }

    private isAuthenticatedUserType(userType: UserType): boolean {
        return Object.values(AUTH_CONFIG_IDS).includes(userType);
    }
}
